import { WeaponMount } from "../../types/weapons";

// Add WeaponType import
interface WeaponType {
  id: string;
  name: string;
  cost: number;
  weight: number;
  power: number;
  crew: number;
  damage: string;
  range: string;
  techLevel: number;
  traits: string[];
  description: string;
  mountType: string;
  ammo?: {
    type: string;
    capacity: number;
    tonsPerReload: number;
    costPerReload: number;
  };
  attackModifier?: number;
  damageMultiple?: number;
  baseSize?: number;
  maxSize?: number;
  intercept?: number;
}

export interface BreakawaySectionWeaponStats {
  sectionId: string;
  sectionName: string;
  sectionSize: number;
  hardpoints: number;
  firmpoints: number;
  hardpointsUsed: number;
  firmpointsUsed: number;
  weaponMounts: WeaponMount[];
  totalCost: number;
  totalWeight: number;
  totalPower: number;
}

export interface BreakawayWeaponCalculations {
  sections: BreakawaySectionWeaponStats[];
  combinedStats: {
    totalCost: number;
    totalWeight: number;
    totalPower: number;
    totalHardpoints: number;
    totalFirmpoints: number;
    totalHardpointsUsed: number;
    totalFirmpointsUsed: number;
  };
}

/**
 * Calculate hardpoints and firmpoints for a section based on Traveller rules
 * @param sectionSize - Size of the section in tons
 * @returns Object with hardpoints and firmpoints
 */
export const calculateSectionWeaponCapacity = (sectionSize: number): { hardpoints: number; firmpoints: number } => {
  // Hardpoints: 1 per 100 tons (Traveller rule)
  const hardpoints = Math.floor(sectionSize / 100);
  
  // Firmpoints for small ships (Traveller rule)
  let firmpoints = 0;
  if (sectionSize < 100) {
    if (sectionSize < 35) {
      firmpoints = 1;
    } else if (sectionSize < 70) {
      firmpoints = 2;
    } else {
      firmpoints = 3;
    }
  }
  
  return { hardpoints, firmpoints };
};

/**
 * Get mount configuration based on type and turret type
 * @param mountType - Type of weapon mount
 * @param turretType - Type of turret (for turret mounts)
 * @returns Mount configuration object
 */
export const getMountConfig = (mountType: string, turretType?: string) => {
  switch (mountType) {
    case "fixed":
      return { hardpointsUsed: 1, tons: 0, cost: 100000, maxWeapons: 3 }; // MCr0.1
    case "turret":
      const maxWeapons = turretType === "single" ? 1 : turretType === "double" ? 2 : 3;
      const turretCost = 
        turretType === "single" ? 200000 : // MCr0.2
        turretType === "double" ? 500000 : // MCr0.5
        1000000; // MCr1 for triple
      return { hardpointsUsed: 1, tons: 1, cost: turretCost, maxWeapons };
    case "barbette":
      return { hardpointsUsed: 1, tons: 5, cost: 2000000, maxWeapons: 1 }; // MCr2
    case "smallBay":
      return { hardpointsUsed: 1, tons: 50, cost: 10000000, maxWeapons: 1 }; // MCr10
    case "mediumBay":
      return { hardpointsUsed: 1, tons: 100, cost: 25000000, maxWeapons: 1 }; // MCr25
    case "largeBay":
      return { hardpointsUsed: 5, tons: 500, cost: 50000000, maxWeapons: 1 }; // MCr50
    case "spinalMount":
      return { hardpointsUsed: 0, tons: 0, cost: 0, maxWeapons: 1 }; // Special handling
    default:
      return { hardpointsUsed: 1, tons: 1, cost: 500000, maxWeapons: 1 };
  }
};

/**
 * Calculate weapon stats for a specific section
 * @param sectionId - ID of the section
 * @param sectionName - Name of the section
 * @param sectionSize - Size of the section in tons
 * @param weaponMounts - Array of weapon mounts for this section
 * @returns Section weapon stats
 */
export const calculateSectionWeaponStats = (
  sectionId: string,
  sectionName: string,
  sectionSize: number,
  weaponMounts: WeaponMount[]
): BreakawaySectionWeaponStats => {
  const { hardpoints, firmpoints } = calculateSectionWeaponCapacity(sectionSize);
  
  const hardpointsUsed = weaponMounts.reduce((total, mount) => total + (mount.hardpointsUsed || 0), 0);
  const firmpointsUsed = weaponMounts.filter(mount => mount.type === "fixed").length;
  
  const totalCost = weaponMounts.reduce((total, mount) => total + (mount.cost || 0), 0);
  const totalWeight = weaponMounts.reduce((total, mount) => total + (mount.tons || 0), 0);
  const totalPower = weaponMounts.reduce((total, mount) => total + (mount.power || 0), 0);

  return {
    sectionId,
    sectionName,
    sectionSize,
    hardpoints,
    firmpoints,
    hardpointsUsed,
    firmpointsUsed,
    weaponMounts,
    totalCost,
    totalWeight,
    totalPower
  };
};

/**
 * Calculate total breakaway weapon statistics across all sections
 * @param sections - Array of breakaway sections with their weapon mounts
 * @returns Combined breakaway weapon calculations
 */
export const calculateBreakawayWeapons = (
  sections: Array<{
    id: string;
    name: string;
    size: number;
    weaponMounts?: WeaponMount[];
  }>
): BreakawayWeaponCalculations => {
  const sectionStats = sections.map(section => 
    calculateSectionWeaponStats(
      section.id,
      section.name,
      section.size,
      section.weaponMounts || []
    )
  );

  const combinedStats = sectionStats.reduce(
    (acc, section) => ({
      totalCost: acc.totalCost + section.totalCost,
      totalWeight: acc.totalWeight + section.totalWeight,
      totalPower: acc.totalPower + section.totalPower,
      totalHardpoints: acc.totalHardpoints + section.hardpoints,
      totalFirmpoints: acc.totalFirmpoints + section.firmpoints,
      totalHardpointsUsed: acc.totalHardpointsUsed + section.hardpointsUsed,
      totalFirmpointsUsed: acc.totalFirmpointsUsed + section.firmpointsUsed,
    }),
    {
      totalCost: 0,
      totalWeight: 0,
      totalPower: 0,
      totalHardpoints: 0,
      totalFirmpoints: 0,
      totalHardpointsUsed: 0,
      totalFirmpointsUsed: 0,
    }
  );

  return {
    sections: sectionStats,
    combinedStats
  };
};

/**
 * Validate weapon mount installation for a section
 * @param sectionSize - Size of the section in tons
 * @param weaponMounts - Current weapon mounts in the section
 * @param newMountType - Type of mount to be added
 * @param newMountQuantity - Quantity of mounts to be added
 * @returns Array of validation errors
 */
export const validateSectionWeaponInstallation = (
  sectionSize: number,
  weaponMounts: WeaponMount[],
  newMountType: string,
  newMountQuantity: number = 1
): string[] => {
  const errors: string[] = [];
  
  const { hardpoints, firmpoints } = calculateSectionWeaponCapacity(sectionSize);
  const currentHardpointsUsed = weaponMounts.reduce((total, mount) => total + (mount.hardpointsUsed || 0), 0);
  const currentFirmpointsUsed = weaponMounts.filter(mount => mount.type === "fixed").length;
  
  const mountConfig = getMountConfig(newMountType);
  const newHardpointsUsed = currentHardpointsUsed + (mountConfig.hardpointsUsed * newMountQuantity);
  const newFirmpointsUsed = currentFirmpointsUsed + (newMountType === "fixed" ? newMountQuantity : 0);
  
  if (newHardpointsUsed > hardpoints) {
    errors.push(`Insufficient hardpoints. Available: ${hardpoints}, Required: ${newHardpointsUsed}`);
  }
  
  if (newFirmpointsUsed > firmpoints) {
    errors.push(`Insufficient firmpoints. Available: ${firmpoints}, Required: ${newFirmpointsUsed}`);
  }
  
  return errors;
};

/**
 * Check if a weapon can be mounted on a specific mount type
 * @param weapon - Weapon to check (WeaponType from constants)
 * @param mountType - Mount type to check against
 * @returns True if weapon can be mounted
 */
export const canWeaponBeMounted = (weapon: WeaponType, mountType: string): boolean => {
  switch (mountType) {
    case "turret":
      return weapon.mountType === "turret";
    case "barbette":
      return weapon.mountType === "barbette";
    case "smallBay":
      return weapon.mountType === "smallBay";
    case "mediumBay":
      return weapon.mountType === "mediumBay";
    case "largeBay":
      return weapon.mountType === "largeBay";
    case "spinalMount":
      return weapon.mountType === "spinalMount";
    case "fixed":
      return weapon.mountType === "turret";
    default:
      return true;
  }
};
